/**
 * useWaMessages — gestiona la carga, paginación y estado de mensajes WhatsApp.
 *
 * Patrón Silicon Valley: cache LRU stale-while-revalidate.
 *   - Click sobre una conv ya visitada → render INSTANT desde cache.
 *   - Refetch en background reconcilia con la BD (con equality guard para
 *     skipear re-render si nada cambió).
 *   - Realtime + mutations optimistas mantienen la cache fresca via wrapper
 *     de setMessages (igual patrón que useWaConversations).
 *
 * Cache vive a nivel de módulo (no del hook) — sobrevive a re-renders y
 * múltiples montajes del provider. LRU 50 convs ≈ memoria del workflow
 * típico de un agente; eviction simple por insertion order del Map.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { traceSpan } from '@/lib/trace';
import { isRenderableMessage } from '@/lib/wa-utils';
import type { WaMessage } from '@/components/whatsapp/WaMessageThread';
import { registrarHilo } from '@/hooks/waThreadRegistry';

// LIMIT inicial bajo (30 vs 60 antes): el viewport típico muestra 8-15
// mensajes; el resto llega por infinite scroll cuando el operador scrollea.
// Menos rows → menos bytes → ~30-40% menos latencia en el primer paint.
const MSG_PAGE_SIZE = 30;
const MSG_SELECT = 'id, direction, sender_type, sender_id, body, msg_type, media_url, media_caption, media_filename, media_status, media_thumbnail, delivery_status, is_internal_note, created_at, reactions, wa_message_id, is_starred, is_edited, edited_at, deleted_at, transcription, transcription_status, transcription_lang, group_participant_phone, group_participant_name, group_participant_jid, order_info, ad_referral, origen';

const MSG_CACHE_MAX = 50;
const messageCache: Map<string, WaMessage[]> = new Map();

function cacheGet(convId: string): WaMessage[] | undefined {
  const v = messageCache.get(convId);
  if (v) {
    // LRU touch: reinserta al final para que la eviction borre la conv
    // menos usada recientemente.
    messageCache.delete(convId);
    messageCache.set(convId, v);
  }
  return v;
}

function cacheSet(convId: string, msgs: WaMessage[]): void {
  messageCache.delete(convId);
  messageCache.set(convId, msgs);
  while (messageCache.size > MSG_CACHE_MAX) {
    const first = messageCache.keys().next().value;
    if (first === undefined) break;
    messageCache.delete(first);
  }
}

/** Equality fast-path: dos listas son "iguales" si tienen los mismos ids
 *  en el mismo orden con los campos que afectan al render. Evita re-render
 *  cuando el refetch de bg trae data sin cambios. */
function areMsgListsEqual(a: WaMessage[], b: WaMessage[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    const ap = a[i];
    const bp = b[i];
    if (ap.id !== bp.id) return false;
    if (ap.body !== bp.body) return false;
    if (ap.delivery_status !== bp.delivery_status) return false;
    if (ap.media_url !== bp.media_url) return false;
    if (ap.media_status !== bp.media_status) return false;
    if (ap.deleted_at !== bp.deleted_at) return false;
    if (ap.is_edited !== bp.is_edited) return false;
    if ((ap.reactions?.length ?? 0) !== (bp.reactions?.length ?? 0)) return false;
  }
  return true;
}

function parseMessages(data: unknown[]): WaMessage[] {
  // Filtro de fantasmas: reactions y bubbles vacíos NUNCA renderizan en
  // WA Web. Si por race del backend / parser cuela una fila, la escondemos
  // acá — single source of truth en `isRenderableMessage`.
  return (data as Record<string, unknown>[])
    .map((m) => ({
      ...m,
      reactions: Array.isArray(m.reactions) ? m.reactions : [],
      wa_message_id: (m.wa_message_id as string) ?? null,
    }))
    .filter((m) => isRenderableMessage(m as unknown as Parameters<typeof isRenderableMessage>[0])) as WaMessage[];
}

interface Options {
  conversationId: string | null;
  tenantId: string | null;
  /** Callback invocado tras marcar el chat como leído (UPDATE en DB).
   *  La página lo usa para actualizar la lista en memoria sin esperar
   *  a que un realtime UPDATE dispare (que puede no escucharse). */
  onMarkAsRead?: (conversationId: string) => void;
}

export function useWaMessages({ conversationId, tenantId, onMarkAsRead }: Options) {
  const [messages, setMessagesRaw] = useState<WaMessage[]>([]);
  const [msgLoading, setMsgLoading] = useState(false);
  const [hasOlderMessages, setHasOlderMessages] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  // Conv activa para descartar fetches stale: si el usuario cambia rápido
  // de A → B → C y A retorna después que C empezó, ignoramos A.
  const activeConvIdRef = useRef<string | null>(null);

  // Wrapper de setMessages: cualquier mutation (optimistic, realtime INSERT,
  // edit, reaction patch) también actualiza la cache. Sin esto, volver a
  // una conv visitada mostraba data divergente hasta el refetch bg.
  const setMessages = useCallback<React.Dispatch<React.SetStateAction<WaMessage[]>>>((updater) => {
    setMessagesRaw((prev) => {
      const next = typeof updater === 'function'
        ? (updater as (p: WaMessage[]) => WaMessage[])(prev)
        : updater;
      if (activeConvIdRef.current) {
        cacheSet(activeConvIdRef.current, next);
      }
      return next;
    });
  }, []);

  /*
   * Anotarse en el registro de hilos abiertos es lo que hace que este hilo reciba los cambios en
   * vivo — mensajes nuevos, checks de entregado y leído, reacciones, ediciones y borrados. Antes
   * eso lo decidía la bandeja comparando contra "la conversación seleccionada", así que cualquier
   * otro montaje del chat quedaba congelado: un mensaje enviado desde una ventana acoplada se
   * quedaba con un solo check aunque el cliente ya lo hubiera leído.
   */
  useEffect(() => {
    if (!conversationId) return;
    return registrarHilo(conversationId, { setMessages, finDelHilo: messagesEndRef });
  }, [conversationId, setMessages]);

  // Load messages for selected conversation
  useEffect(() => {
    if (!conversationId || !tenantId) {
      activeConvIdRef.current = null;
      setMessagesRaw([]);
      setHasOlderMessages(false);
      return;
    }
    activeConvIdRef.current = conversationId;

    // Cache hit → render INSTANT, sin spinner. Refetch en background para
    // reconciliar (mensajes nuevos llegados via realtime ya están aplicados,
    // pero capturamos cambios de delivery_status / edits / nuevos rows que
    // el realtime pudo perderse).
    const cached = cacheGet(conversationId);
    if (cached) {
      setMessagesRaw(cached);
      setHasOlderMessages(cached.length >= MSG_PAGE_SIZE);
      setMsgLoading(false);
      // Scroll suave hacia abajo solo en cache hit — sin parpadeo.
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'auto' }), 30);
    } else {
      setMessagesRaw([]);
      setHasOlderMessages(false);
      setMsgLoading(true);
    }

    (async () => {
      await traceSpan('wa.thread.load', { tenant_id: tenantId, conversation_id: conversationId, page_size: MSG_PAGE_SIZE }, async () => {
        const { data } = await supabase
          .from('wa_messages')
          .select(MSG_SELECT)
          .eq('conversation_id', conversationId)
          .eq('tenant_id', tenantId)
          .order('created_at', { ascending: false })
          .limit(MSG_PAGE_SIZE);
        // Descarta si el usuario ya cambió a otra conv mientras este SELECT
        // estaba en vuelo. Sin esto, A → B → C podría pintar A sobre C.
        if (activeConvIdRef.current !== conversationId) return;
        const msgs = parseMessages((data ?? []).reverse());
        const hasOlder = (data ?? []).length >= MSG_PAGE_SIZE;
        cacheSet(conversationId, msgs);
        // Equality guard: si nada cambió respecto al cache renderizado,
        // skipeamos el setMessages para no disparar re-render del thread.
        setMessagesRaw((prev) => areMsgListsEqual(prev, msgs) ? prev : msgs);
        setHasOlderMessages(hasOlder);
        setMsgLoading(false);
        if (!cached) {
          // Solo auto-scroll en miss inicial — en hit ya hicimos el scroll
          // arriba contra la cache, y un segundo scroll aquí se sentiría
          // como "salto" para el operador que ya estaba leyendo.
          setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'auto' }), 50);
        }
      });

      // Mark as read en DB
      await supabase.from('wa_conversations').update({ unread_count: 0, updated_at: new Date().toISOString() }).eq('id', conversationId);
      // Notificar a la página para que actualice la lista en memoria
      // (el realtime no escucha UPDATE de wa_conversations).
      onMarkAsRead?.(conversationId);

      // Send read receipt (fire-and-forget)
      const session = (await supabase.auth.getSession()).data.session;
      if (session) {
        fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/whatsapp-read-receipt`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({ conversation_id: conversationId }),
        }).catch(() => {});
      }
    })();
  }, [conversationId, tenantId]);

  // Load older messages (infinite scroll)
  const handleLoadOlderMessages = useCallback(async () => {
    if (!conversationId || !tenantId || loadingOlder || messages.length === 0) return;
    setLoadingOlder(true);
    await traceSpan('wa.thread.older', { tenant_id: tenantId, conversation_id: conversationId, page_size: MSG_PAGE_SIZE }, async () => {
      const oldest = messages[0].created_at;
      const { data } = await supabase
        .from('wa_messages')
        .select(MSG_SELECT)
        .eq('conversation_id', conversationId)
        .eq('tenant_id', tenantId)
        .lt('created_at', oldest)
        .order('created_at', { ascending: false })
        .limit(MSG_PAGE_SIZE);
      const older = parseMessages((data ?? []).reverse());
      if (older.length < MSG_PAGE_SIZE) setHasOlderMessages(false);
      if (older.length > 0) {
        setMessages((prev) => [...older, ...prev]);
      }
      setLoadingOlder(false);
    });
  }, [conversationId, tenantId, loadingOlder, messages, setMessages]);

  /**
   * Carga una ventana de mensajes centrada en `messageId`. Usado por la
   * búsqueda cuando el mensaje target está fuera del window actual.
   * Devuelve `true` si el mensaje quedó cargado, `false` si no se encontró.
   */
  const loadMessagesAround = useCallback(async (messageId: string): Promise<boolean> => {
    if (!conversationId || !tenantId) return false;

    // Si el mensaje ya está en memoria, no recargamos
    if (messages.some((m) => m.id === messageId)) return true;

    const { data: target } = await supabase
      .from('wa_messages')
      .select('created_at')
      .eq('id', messageId)
      .eq('conversation_id', conversationId)
      .eq('tenant_id', tenantId)
      .maybeSingle();
    if (!target?.created_at) return false;

    const half = Math.floor(MSG_PAGE_SIZE / 2);
    const [olderRes, newerRes] = await Promise.all([
      supabase
        .from('wa_messages')
        .select(MSG_SELECT)
        .eq('conversation_id', conversationId)
        .eq('tenant_id', tenantId)
        .lte('created_at', target.created_at)
        .order('created_at', { ascending: false })
        .limit(half + 1), // +1 incluye el target
      supabase
        .from('wa_messages')
        .select(MSG_SELECT)
        .eq('conversation_id', conversationId)
        .eq('tenant_id', tenantId)
        .gt('created_at', target.created_at)
        .order('created_at', { ascending: true })
        .limit(half),
    ]);

    const older = parseMessages((olderRes.data ?? []).reverse());
    const newer = parseMessages(newerRes.data ?? []);
    setMessages([...older, ...newer]);
    setHasOlderMessages((olderRes.data ?? []).length > half);
    return true;
  }, [conversationId, tenantId, messages, setMessages]);

  return {
    messages,
    setMessages,
    msgLoading,
    hasOlderMessages,
    setHasOlderMessages,
    loadingOlder,
    handleLoadOlderMessages,
    loadMessagesAround,
    messagesEndRef,
  };
}

export { MSG_PAGE_SIZE, MSG_SELECT, parseMessages };
